/**
 * The apothecary's fitted timberwork (lifted from lantern-water's
 * `shop-joinery.js`): raised-panel dados along the three walls, the stepped
 * cornice of the full-height wall, and the hanging herb cabinet with its
 * stock and brackets. The darker panel paint is a kit-shared clone of the
 * cabinet paint; no bitmap or light of its own.
 */
import * as THREE from 'three';
import { apothecaryVessel, cabinetPaint } from './apothecary.ts';
import type { Kit } from './kit.ts';

const panelPaint = (kit: Kit): THREE.MeshStandardMaterial =>
  kit.shared('shop-joinery:paint', () => {
    const paint = cabinetPaint(kit).clone();
    paint.color.set('#64786d');
    paint.roughness = 0.86;
    return paint;
  });

const block = (kit: Kit, parent: THREE.Object3D, material: THREE.Material, x: number, y: number, z: number, width: number, height: number, depth: number): THREE.Mesh => {
  const mesh = new THREE.Mesh(kit.geo.cube, material);
  mesh.position.set(x, y, z);
  mesh.scale.set(width, height, depth);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  parent.add(mesh);
  return mesh;
};

const raisedPanel = (width: number, height: number): THREE.ExtrudeGeometry => {
  const shape = new THREE.Shape();
  const c = 0.07;
  shape.moveTo(-width / 2 + c, -height / 2);
  shape.lineTo(width / 2 - c, -height / 2);
  shape.lineTo(width / 2, -height / 2 + c);
  shape.lineTo(width / 2, height / 2 - c);
  shape.lineTo(width / 2 - c, height / 2);
  shape.lineTo(-width / 2 + c, height / 2);
  shape.lineTo(-width / 2, height / 2 - c);
  shape.lineTo(-width / 2, -height / 2 + c);
  shape.closePath();
  const geometry = new THREE.ExtrudeGeometry(shape, { depth: 0.018, steps: 1, bevelEnabled: true, bevelSegments: 1, bevelSize: 0.018, bevelThickness: 0.015, curveSegments: 1 });
  const positions = geometry.getAttribute('position');
  const uv = geometry.getAttribute('uv');
  for (let i = 0; i < uv.count; i += 1) uv.setXY(i, (positions.getX(i) + width / 2 + 0.018) / (width + 0.036), (positions.getY(i) + height / 2 + 0.018) / (height + 0.036));
  return geometry;
};

const dado = (kit: Kit, parent: THREE.Object3D, length: number): void => {
  const m = kit.m;
  const paint = panelPaint(kit);
  block(kit, parent, paint, 0, 0.66, 0.055, length, 1.12, 0.1);
  const count = Math.round(length / 1.12);
  const spacing = length / count;
  const panel = raisedPanel(spacing - 0.23, 0.69);
  for (let i = 0; i < count; i += 1) {
    const x = -length / 2 + (i + 0.5) * spacing;
    block(kit, parent, m.darkWood, x, 0.65, 0.113, spacing - 0.15, 0.79, 0.015);
    const mesh = new THREE.Mesh(panel, paint);
    mesh.position.set(x, 0.65, 0.13);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    parent.add(mesh);
    block(kit, parent, m.wood, x - spacing / 2 + 0.055, 0.67, 0.135, 0.075, 1.0, 0.07);
  }
  for (const [y, height, depth] of [
    [0.16, 0.18, 0.17],
    [0.28, 0.055, 0.19],
    [1.11, 0.075, 0.17],
    [1.18, 0.06, 0.21],
  ] as const) {
    block(kit, parent, m.wood, 0, y, depth / 2, length, height, depth);
  }
};

/** The dados, cornice and hanging cabinet, in the room's coordinates. */
export const shopJoinery = (kit: Kit, parent: THREE.Object3D): void => {
  const m = kit.m;
  for (const [x, z, length, angle] of [
    [-6.49, 0, 10.4, Math.PI / 2],
    [0, -5.1, 13.0, 0],
    [6.49, -2.5, 4.9, -Math.PI / 2],
  ] as const) {
    const wall = new THREE.Group();
    wall.position.set(x, 0, z);
    wall.rotation.y = angle;
    parent.add(wall);
    dado(kit, wall, length);
  }
  for (const [y, depth, height] of [
    [4.43, 0.16, 0.055],
    [4.49, 0.22, 0.065],
    [4.55, 0.27, 0.045],
  ] as const) {
    block(kit, parent, m.wood, -6.49 + depth / 2, y, 0, depth, height, 10.4);
  }
  const cabinet = new THREE.Group();
  cabinet.position.set(-6.39, 2.16, 3.6);
  cabinet.name = 'hanging-herb-cabinet';
  cabinet.rotation.y = Math.PI / 2;
  parent.add(cabinet);
  block(kit, cabinet, panelPaint(kit), 0, 0.76, 0.035, 1.86, 1.52, 0.07);
  for (const side of [-1, 1]) {
    block(kit, cabinet, cabinetPaint(kit), side * 0.89, 0.76, 0.09, 0.1, 1.52, 0.18);
    block(kit, cabinet, m.wood, side * 0.89, 0.76, 0.51, 0.06, 1.52, 0.045);
  }
  for (const y of [0.04, 0.54, 1.04, 1.54]) {
    block(kit, cabinet, m.wood, 0, y, 0.28, 1.9, 0.075, 0.57).name = 'cabinet-shelf';
    block(kit, cabinet, m.darkWood, 0, y - 0.047, 0.52, 1.83, 0.035, 0.06);
  }
  block(kit, cabinet, m.wood, 0, 1.62, 0.23, 2.04, 0.08, 0.61);
  block(kit, cabinet, m.darkWood, 0, 1.68, 0.23, 2.12, 0.04, 0.65);
  const scales = [0.19, 0.2, 0.17];
  for (let row = 0; row < 3; row += 1) {
    for (let i = 0; i < 3; i += 1) {
      const kind = (row * 3 + i + 1) % 4;
      const stock = new THREE.Group();
      stock.name = 'cabinet-stock';
      cabinet.add(stock);
      apothecaryVessel(kit, stock, (i - 1) * 0.54, 0.083 + row * 0.5, 0.44, scales[(row + i) % 3] ?? 0.19, kind);
    }
  }
  const bracketGeo = kit.shared('shop-joinery:bracket', () => {
    const bracketShape = new THREE.Shape();
    bracketShape.moveTo(0, 0);
    bracketShape.lineTo(0.4, 0);
    bracketShape.quadraticCurveTo(0.14, -0.08, 0, -0.23);
    bracketShape.closePath();
    return new THREE.ExtrudeGeometry(bracketShape, { depth: 0.07, bevelEnabled: false, curveSegments: 6 });
  });
  for (const side of [-1, 1]) {
    const bracket = new THREE.Mesh(bracketGeo, m.wood);
    bracket.rotation.y = -Math.PI / 2;
    bracket.position.set(side * 0.66 + 0.035, 0, 0.04);
    bracket.castShadow = true;
    bracket.receiveShadow = true;
    cabinet.add(bracket);
  }
};
